import type { ClusterId, ClusterLevelId, OverallBandId } from '../types/ids';

/**
 * Kontrollierte Ergebnistexte (Abschnitt 16.7). Die App generiert zur Laufzeit keine freien
 * Formulierungen – alle sichtbaren Ergebnistexte stammen aus dieser Datei bzw. `questionResultCopy.ts`.
 */

export interface OverallBandCopy {
  headline: string;
  detail: string;
}

export const OVERALL_BAND_COPY: Record<OverallBandId, OverallBandCopy> = {
  wrong_primary_channel: {
    headline: 'Instagram passt aktuell eher nicht als wichtigster Kanal.',
    detail:
      'Mehrere grundlegende Voraussetzungen – etwa Zielgruppenaktivität, ein erprobtes Angebot oder ein klarer Verkaufsweg – sind noch nicht ausreichend gegeben. Das ist keine Bewertung deiner Person oder deines Produkts, sondern ein Hinweis darauf, dass entweder zuerst Grundlagen aufgebaut oder ein anderer Kanal geprüft werden sollte.',
  },
  supporting_channel: {
    headline: 'Instagram kann eine ergänzende Rolle übernehmen.',
    detail:
      'Einzelne Bereiche passen bereits gut, andere benötigen noch Aufbauarbeit. Instagram sollte in dieser Ausgangslage wahrscheinlich nicht den gesamten Verkauf tragen, kann aber gezielt für Sichtbarkeit, Vertrauen oder einzelne Aufgaben genutzt werden.',
  },
  suitable_with_conditions: {
    headline: 'Instagram ist geeignet, wenn du einige Voraussetzungen bewusst löst.',
    detail:
      'Die Ausgangslage ist insgesamt gut. Gleichzeitig gibt es einzelne Engpässe oder Spannungsfelder, die den Erfolg spürbar begrenzen können, solange sie ungeklärt bleiben. Werden diese Punkte gezielt angegangen, steigen die Erfolgsaussichten deutlich.',
  },
  strong_potential_fit: {
    headline: 'Instagram kann eine wichtige Rolle in deinem Geschäft übernehmen.',
    detail:
      'Zielgruppe, Thema, Vertrauen, Alltag und Verkaufsvoraussetzungen passen insgesamt sehr gut zusammen. Damit aus dieser guten Ausgangslage auch wirtschaftlicher Erfolg wird, sollten die guten Voraussetzungen konsequent umgesetzt und ihre Wirkung laufend überprüft werden.',
  },
};

export interface ClusterLevelCopy {
  summary: string;
  detail: string;
  opportunities: string[];
  risks: string[];
}

type ClusterCopyTable = Record<ClusterId, Record<ClusterLevelId, ClusterLevelCopy>>;

export const CLUSTER_LEVEL_COPY: ClusterCopyTable = {
  audience_demand: {
    very_low: {
      summary: 'Die aktuelle Zielgruppe scheint auf Instagram kaum erreichbar zu sein.',
      detail:
        'Weder die Aktivität der Zielgruppe noch die Feed-Tauglichkeit des Themas sprechen aktuell für Instagram als Ort, an dem deine Interessenten aufmerksam werden. Bevor mehr Inhalte entstehen, lohnt sich eine gezielte Prüfung, wo deine Zielgruppe tatsächlich Zeit verbringt.',
      opportunities: ['Alternative Kanäle mit echter Zielgruppenpräsenz identifizieren.'],
      risks: ['Inhalte könnten an den eigentlich passenden Menschen vorbeigehen.'],
    },
    low: {
      summary: 'Nur ein kleinerer Teil der Zielgruppe scheint über Instagram erreichbar.',
      detail:
        'Es gibt einzelne Hinweise auf Erreichbarkeit, aber weder starke Aktivität noch eine klare Feed-Eignung des Themas. Instagram sollte hier eher getestet als vorausgesetzt werden.',
      opportunities: ['Mit einzelnen Formaten testen, ob echte Reaktionen entstehen.'],
      risks: ['Aufwand und tatsächliche Reichweite könnten auseinanderfallen.'],
    },
    medium: {
      summary: 'Zielgruppe und Thema passen teilweise zu Instagram.',
      detail:
        'Einzelne Voraussetzungen sind erfüllt, andere noch nicht. Es lohnt sich, genauer zu beobachten, welche Themen bei deiner Zielgruppe tatsächlich auf Interesse stoßen.',
      opportunities: ['Gezielt beobachten, welche Beiträge echte Anfragen auslösen.'],
      risks: ['Ohne Beobachtung bleibt unklar, ob sich der Aufwand lohnt.'],
    },
    high: {
      summary: 'Deine Zielgruppe ist grundsätzlich gut über Instagram erreichbar.',
      detail:
        'Sowohl die Aktivität der Zielgruppe als auch die Feed-Eignung deines Themas sprechen für Instagram. Diese Voraussetzung ist eine der wichtigsten Grundlagen für einen funktionierenden Kanal.',
      opportunities: ['Reichweite gezielt für Vertrauen und Sichtbarkeit nutzen.'],
      risks: ['Ohne Verkaufsweg bleibt Aufmerksamkeit wirtschaftlich wirkungslos.'],
    },
    very_high: {
      summary: 'Deine Zielgruppe ist sehr gut über Instagram erreichbar.',
      detail:
        'Zielgruppenaktivität und Feed-Eignung sprechen deutlich für Instagram als sinnvollen Kanal. Diese starke Ausgangslage sollte konsequent für Sichtbarkeit und Vertrauensaufbau genutzt werden.',
      opportunities: ['Aufmerksamkeit systematisch in Kontakte und Verkäufe überführen.'],
      risks: ['Eine sehr gute Zielgruppen-Passung ersetzt keinen fehlenden Verkaufsweg.'],
    },
  },
  topic_content: {
    very_low: {
      summary: 'Das Thema lässt sich aktuell nur schwer in Instagram-Inhalte übersetzen.',
      detail:
        'Weder die Darstellung des Themas noch das Ergebnis oder der Themenvorrat sprechen aktuell für einen visuellen, wiederkehrenden Kanal. Das kann bedeuten, dass ein anderes Format oder ein anderer Kanal näher am Thema liegt.',
      opportunities: ['Prüfen, ob Text- oder audiobasierte Formate besser passen.'],
      risks: ['Inhalte könnten unverständlich oder wenig greifbar wirken.'],
    },
    low: {
      summary: 'Das Thema lässt sich nur mit deutlichem Zusatzaufwand zeigen.',
      detail:
        'Einzelne Aspekte lassen sich darstellen, ein durchgängiger visueller roter Faden fehlt aber noch. Das erhöht den Aufwand pro Beitrag spürbar.',
      opportunities: ['Wenige, dafür besonders klare Kernformate entwickeln.'],
      risks: ['Hoher Erklärungsaufwand kann die Regelmäßigkeit gefährden.'],
    },
    medium: {
      summary: 'Thema und Inhalte lassen sich mit Aufwand in einen Kanal übersetzen.',
      detail:
        'Es gibt brauchbare Ansatzpunkte für Darstellung, Ergebnis und Themenvorrat, aber noch keinen durchgängig einfachen Ablauf.',
      opportunities: ['Wiederkehrende Formate für die stärksten Themen festlegen.'],
      risks: ['Ohne festen Ablauf bleibt die Inhaltserstellung aufwendig.'],
    },
    high: {
      summary: 'Dein Thema lässt sich gut in Instagram-Inhalte übersetzen.',
      detail:
        'Darstellung, Ergebnis und Themenvorrat sprechen dafür, dass sich dauerhaft verständliche und abwechslungsreiche Inhalte erstellen lassen.',
      opportunities: ['Vorhandenen Themenvorrat in einen festen Redaktionsplan überführen.'],
      risks: ['Guter Inhalt-Fit ersetzt kein fehlendes Produktionssystem.'],
    },
    very_high: {
      summary: 'Dein Thema ist sehr gut für Instagram-Inhalte geeignet.',
      detail:
        'Das Thema lässt sich sichtbar erklären, das Ergebnis zeigen und es besteht ein großer, eigenständiger Themenvorrat. Das senkt den langfristigen Erklärungsaufwand erheblich.',
      opportunities: ['Format-Vielfalt nutzen, um verschiedene Teile der Zielgruppe zu erreichen.'],
      risks: ['Ein großer Themenvorrat hilft wenig ohne verlässlichen Produktionsablauf.'],
    },
  },
  trust_visibility: {
    very_low: {
      summary: 'Vertrauen und Sichtbarkeit passen aktuell kaum zusammen.',
      detail:
        'Weder die Darstellung deines Themas noch dein Vertrauensbedarf und deine Bereitschaft zu Sichtbarkeit sprechen aktuell für einen stark vertrauensbasierten Auftritt.',
      opportunities: ['Nachvollziehbare Belege statt persönlicher Sichtbarkeit aufbauen.'],
      risks: ['Vertrauen könnte langsamer entstehen als für den Verkauf nötig.'],
    },
    low: {
      summary: 'Vertrauen und Sichtbarkeit sind derzeit eher schwach ausgeprägt.',
      detail:
        'Es fehlen noch einzelne Bausteine, um glaubwürdiges Vertrauen über Instagram aufzubauen, etwa Beispiele, eine klare Methode oder eine tragfähige Form der Sichtbarkeit.',
      opportunities: ['Fallbeispiele und Arbeitsproben gezielt aufbauen.'],
      risks: ['Ohne Vertrauenssignale kann die Kaufbereitschaft niedrig bleiben.'],
    },
    medium: {
      summary: 'Vertrauen und Sichtbarkeit ergeben ein gemischtes Bild.',
      detail:
        'Einzelne Voraussetzungen passen bereits, etwa Teile der Sichtbarkeit oder erste Belege. Andere, etwa der Umgang mit Reaktionen, benötigen noch bewusste Arbeit.',
      opportunities: ['Ein klar erkennbares Sichtbarkeitsmodell festlegen.'],
      risks: ['Ohne Klarheit über die Sichtbarkeitsform bleibt der Auftritt uneinheitlich.'],
    },
    high: {
      summary: 'Vertrauen und Sichtbarkeit passen bereits gut zusammen.',
      detail:
        'Die Bereitschaft zu Sichtbarkeit und der Umgang mit Vertrauen und Reaktionen sind überwiegend tragfähig. Das ist eine gute Grundlage für einen glaubwürdigen Auftritt.',
      opportunities: ['Wiederkehrende Vertrauenssignale wie Methode und Beispiele ausbauen.'],
      risks: ['Ohne bewusste Grenzen kann Sichtbarkeit trotzdem belastend werden.'],
    },
    very_high: {
      summary: 'Vertrauen und Sichtbarkeit passen sehr gut zusammen.',
      detail:
        'Dein Vertrauensbedarf und deine Bereitschaft zur Sichtbarkeit sowie der Umgang mit öffentlichen Reaktionen ergänzen sich sehr gut. Das ist eine starke Grundlage für Glaubwürdigkeit.',
      opportunities: ['Persönliche Sichtbarkeit gezielt für Vertrauensaufbau einsetzen.'],
      risks: ['Auch bei starker Sichtbarkeit bleiben klare Grenzen wichtig.'],
    },
  },
  daily_execution: {
    very_low: {
      summary: 'Die regelmäßige Umsetzung im Alltag ist aktuell nicht tragfähig.',
      detail:
        'Weder Kommunikationsbereitschaft noch Zeit und Ablauf sprechen aktuell dafür, dass Instagram dauerhaft ohne Überlastung bespielt werden kann.',
      opportunities: ['Aufwand und Formate radikal reduzieren, bevor mehr investiert wird.'],
      risks: ['Ohne tragfähigen Ablauf droht ein schneller Abbruch.'],
    },
    low: {
      summary: 'Die Umsetzung im Alltag ist eher schwach ausgeprägt.',
      detail:
        'Einzelne Voraussetzungen wie Zeit oder ein verlässlicher Ablauf fehlen noch, auch wenn die grundsätzliche Bereitschaft vorhanden sein kann.',
      opportunities: ['Ein einfaches, wiederholbares Zeitfenster pro Woche festlegen.'],
      risks: ['Unregelmäßigkeit kann Vertrauensaufbau und Sichtbarkeit ausbremsen.'],
    },
    medium: {
      summary: 'Die Umsetzung im Alltag ist grundsätzlich möglich, aber noch nicht stabil.',
      detail:
        'Kommunikationsbereitschaft, Zeit und der Umgang mit Reaktionen sind teilweise vorhanden, ein verlässlicher Ablauf fehlt jedoch noch.',
      opportunities: ['Inhalte gebündelt statt täglich neu produzieren.'],
      risks: ['Ohne festen Ablauf bleibt die Umsetzung von der Tagesform abhängig.'],
    },
    high: {
      summary: 'Die Umsetzung im Alltag ist bereits gut tragfähig.',
      detail:
        'Kommunikationsbereitschaft, Zeitbudget und der Umgang mit Reaktionen passen überwiegend zusammen. Das spricht für eine dauerhaft realistische Umsetzung.',
      opportunities: ['Bestehenden Ablauf für mehr Konstanz feinjustieren.'],
      risks: ['Auch ein guter Ablauf kann ohne Auswertung ineffizient bleiben.'],
    },
    very_high: {
      summary: 'Die Umsetzung im Alltag ist sehr tragfähig.',
      detail:
        'Kommunikationsbereitschaft, verfügbare Zeit und der Umgang mit öffentlicher Resonanz passen sehr gut zusammen. Das ist eine starke Grundlage für einen dauerhaften Kanal.',
      opportunities: ['Freie Kapazität für Qualität statt zusätzliche Formate nutzen.'],
      risks: ['Eine hohe Belastbarkeit ersetzt keine wirtschaftliche Auswertung.'],
    },
  },
  sales_readiness: {
    very_low: {
      summary: 'Angebot und Verkaufsweg sind aktuell nicht ausreichend vorbereitet.',
      detail:
        'Weder eine erprobte Nachfrage noch ein klarer Verkaufsweg oder eine Möglichkeit zur Erfolgsmessung sind aktuell gegeben. Instagram könnte hier Aufmerksamkeit erzeugen, ohne dass daraus Verkäufe entstehen.',
      opportunities: ['Zuerst das Angebot mit echten Interessenten prüfen.'],
      risks: ['Reichweite ohne Verkaufsweg bleibt wirtschaftlich wirkungslos.'],
    },
    low: {
      summary: 'Angebot und Verkaufsweg sind eher schwach ausgeprägt.',
      detail:
        'Es gibt erste Signale, aber weder ein vollständig erprobtes Angebot noch einen durchgängigen Ablauf vom Inhalt bis zum Kauf.',
      opportunities: ['Einen einzelnen, klaren nächsten Schritt definieren.'],
      risks: ['Interessenten könnten ohne klaren Weg wieder abspringen.'],
    },
    medium: {
      summary: 'Angebot und Verkauf sind teilweise vorbereitet.',
      detail:
        'Einzelne Bausteine wie erste Verkäufe oder Teile des Verkaufswegs sind vorhanden, eine durchgängige und messbare Struktur fehlt jedoch noch.',
      opportunities: ['Herkunft von Anfragen und Verkäufen einfach festhalten.'],
      risks: ['Ohne Messung bleibt unklar, ob sich der Aufwand lohnt.'],
    },
    high: {
      summary: 'Angebot und Verkaufsweg sind bereits gut vorbereitet.',
      detail:
        'Das Angebot ist überwiegend erprobt, ein Verkaufsweg ist erkennbar und die wirtschaftliche Wirkung lässt sich zumindest teilweise nachvollziehen.',
      opportunities: ['Den bestehenden Verkaufsweg weiter vereinfachen und dokumentieren.'],
      risks: ['Ohne laufende Messung kann sich die Wirkung trotzdem verschlechtern.'],
    },
    very_high: {
      summary: 'Angebot und Verkaufsweg sind sehr gut vorbereitet.',
      detail:
        'Das Angebot ist erprobt, der Verkaufsweg ist klar aufgebaut und die wirtschaftliche Wirkung lässt sich nachvollziehen. Das ist eine starke Grundlage für Instagram als Kanal.',
      opportunities: ['Den bewährten Verkaufsweg auf weitere Formate übertragen.'],
      risks: ['Auch ein starker Verkaufsweg braucht laufende Beobachtung der Ergebnisse.'],
    },
  },
  instagram_independence: {
    very_low: {
      summary: 'Das Geschäft wäre aktuell fast vollständig von Instagram abhängig.',
      detail:
        'Ohne eigene Kontaktwege könnte ein Ausfall oder eine Änderung der Reichweite den Zugang zu Interessenten unmittelbar gefährden.',
      opportunities: ['Als ersten Schritt eine einfache E-Mail-Liste aufbauen.'],
      risks: ['Änderungen bei Instagram könnten das Geschäft direkt treffen.'],
    },
    low: {
      summary: 'Die Unabhängigkeit von Instagram ist eher schwach ausgeprägt.',
      detail:
        'Es gibt kaum eigene Kontaktwege außerhalb von Instagram, was das Geschäft anfällig für Schwankungen der Reichweite macht.',
      opportunities: ['Einen zweiten Kontaktweg wie Website oder E-Mail aufbauen.'],
      risks: ['Sinkende Reichweite würde direkt den Zugang zu Interessenten verringern.'],
    },
    medium: {
      summary: 'Es besteht eine teilweise Unabhängigkeit von Instagram.',
      detail:
        'Ein weiterer Weg zur Zielgruppe ist vorhanden, ersetzt Instagram aber noch nicht vollständig.',
      opportunities: ['Den vorhandenen zweiten Kontaktweg gezielt stärker nutzen.'],
      risks: ['Bei starkem Instagram-Rückgang bliebe nur ein begrenzter Ausweich-Kanal.'],
    },
    high: {
      summary: 'Das Geschäft ist bereits gut unabhängig von Instagram aufgestellt.',
      detail:
        'Es bestehen eigene oder zusätzliche Kontaktwege, die auch bei schwankender Instagram-Reichweite eine gewisse Stabilität ermöglichen.',
      opportunities: ['Eigene Kanäle wie Website oder E-Mail-Liste weiter ausbauen.'],
      risks: ['Ohne laufende Pflege können auch eigene Kanäle an Wirkung verlieren.'],
    },
    very_high: {
      summary: 'Das Geschäft ist sehr gut unabhängig von Instagram aufgestellt.',
      detail:
        'Eigene Kontaktwege und bestehende Kundenbeziehungen ermöglichen es, Interessenten auch ohne Instagram zuverlässig zu erreichen.',
      opportunities: ['Instagram gezielt als zusätzlichen, aber nicht alleinigen Kanal nutzen.'],
      risks: ['Auch stabile eigene Kanäle profitieren von regelmäßiger Pflege.'],
    },
  },
};

export const NO_TENSIONS_DETECTED_TEXT =
  'Es wurden keine deutlichen Widersprüche zwischen deinen Antworten erkannt.';
